import React, { useState } from 'react';
import {
  Box, Typography, Button, IconButton, Tooltip,
  FormControl, Select, MenuItem, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions,
  useTheme, useMediaQuery,
} from '@mui/material';
import {
  PlayArrow, Pause, ChevronLeft, ChevronRight,
  AccessTime, Sync, ZoomOut, ZoomIn,
  Label as LabelIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { LokiServer, LOGO_BASE, MODULE_LOGO_MAP, QUICK_RANGES, liveBlinkAnimation } from '../types';
import { LiveRefreshCountdown } from './LiveRefreshCountdown';

interface LogToolbarProps {
  lokiServers: LokiServer[];
  selectedServer: string;
  onServerChange: (serverId: string) => void;
  /** Shown when the server list is empty (dropdown placeholder + disabled menu item). */
  serverListEmptyHint?: string;
  selectedLabelCount: number;
  onOpenLabelDialog: () => void;
  isSelectedServerLoki: boolean;
  timeRangeLabel: string;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onShiftBack: () => void;
  onShiftForward: () => void;
  onTimePickerOpen: (e: React.MouseEvent<HTMLElement>) => void;
  onRunQuery: () => void;
  logQueryLoading: boolean;
  isLive: boolean;
  onToggleLive: () => void;
  /** Epoch ms of the next live fetch. Null while live polling is off. */
  nextFetchAt: number | null;
  chartZoomRange: { from: number; to: number } | null;
}

export const LogToolbar: React.FC<LogToolbarProps> = React.memo(({
  lokiServers,
  selectedServer,
  onServerChange,
  serverListEmptyHint = 'No Loki servers configured',
  selectedLabelCount,
  onOpenLabelDialog,
  isSelectedServerLoki,
  timeRangeLabel,
  onZoomIn,
  onZoomOut,
  onShiftBack,
  onShiftForward,
  onTimePickerOpen,
  onRunQuery,
  logQueryLoading,
  isLive,
  onToggleLive,
  nextFetchAt,
  chartZoomRange,
}) => {
  const theme = useTheme();
  const isLightToolbar = theme.palette.mode === 'light';
  /** Light themes: secondary gold remaps to low-contrast greys on toolbar paper. */
  const toolbarAccentColor = isLightToolbar ? 'rgb(25, 25, 25)' : 'secondary.main';
  const toolbarAccentBorder = isLightToolbar ? 'rgba(25, 25, 25, 0.38)' : 'secondary.main';
  const timeRangeIconSx = {
    border: '1px solid',
    borderColor: toolbarAccentBorder,
    color: toolbarAccentColor,
    borderRadius: 1,
    ...(isLightToolbar && {
      '&.Mui-disabled': {
        borderColor: 'rgba(25, 25, 25, 0.26)',
        color: 'rgba(25, 25, 25, 0.38)',
      },
    }),
  };
  const timeRangePickerButtonSx = {
    textTransform: 'none' as const,
    fontWeight: 600,
    fontSize: '0.82rem',
    borderColor: toolbarAccentBorder,
    color: toolbarAccentColor,
    px: 1.5,
    whiteSpace: 'nowrap' as const,
  };
  const isMobilePortrait = useMediaQuery(theme.breakpoints.down('sm'));
  const isLandscapeMobile = useMediaQuery('(max-height: 500px) and (orientation: landscape)');
  const isCompact = isMobilePortrait || isLandscapeMobile;
  const [toolbarDialogOpen, setToolbarDialogOpen] = useState(false);

  const timeRangeControls = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
      <Tooltip title="Zoom in time range">
        <span>
          <IconButton
            size="small"
            onClick={onZoomIn}
            disabled={timeRangeLabel === QUICK_RANGES[0].label}
            sx={timeRangeIconSx}
          >
            <ZoomIn fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Shift time range back">
        <IconButton size="small" onClick={onShiftBack} sx={timeRangeIconSx}>
          <ChevronLeft fontSize="small" />
        </IconButton>
      </Tooltip>

      <Button
        size="small"
        variant="outlined"
        color={isLightToolbar ? undefined : 'secondary'}
        onClick={onTimePickerOpen}
        startIcon={<AccessTime sx={{ fontSize: 16 }} />}
        sx={timeRangePickerButtonSx}
      >
        {timeRangeLabel}
      </Button>

      <Tooltip title="Shift time range forward">
        <IconButton size="small" onClick={onShiftForward} sx={timeRangeIconSx}>
          <ChevronRight fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Zoom out time range">
        <IconButton size="small" onClick={onZoomOut} sx={timeRangeIconSx}>
          <ZoomOut fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const actionButtons = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: isCompact ? 'stretch' : 'flex-end' }}>
      <Button
        variant="contained"
        color="secondary"
        size="small"
        startIcon={<Sync sx={{ fontSize: 16 }} />}
        onClick={onRunQuery}
        sx={{ textTransform: 'none', fontWeight: 700, px: 2, whiteSpace: 'nowrap', ...(isCompact && { flex: 1 }) }}
        disabled={!selectedServer || logQueryLoading}
      >
        {logQueryLoading ? 'Loading...' : 'Run query'}
      </Button>

      <LiveRefreshCountdown nextFetchAt={nextFetchAt} />

      <Button
        variant={isLive ? 'contained' : 'outlined'}
        color={isLive ? 'success' : 'secondary'}
        size="small"
        startIcon={isLive ? <Pause sx={{ fontSize: 16 }} /> : <PlayArrow sx={{ fontSize: 16 }} />}
        onClick={onToggleLive}
        sx={{
          textTransform: 'none', fontWeight: 600, px: 1.5,
          whiteSpace: 'nowrap',
          ...(isLive && {
            animation: `${liveBlinkAnimation} 1.5s ease-in-out infinite`,
            backgroundColor: 'success.main',
            color: '#fff',
            '&:hover': { backgroundColor: 'success.dark' },
          }),
          ...(isCompact && { flex: 1 }),
        }}
        disabled={!selectedServer || !!chartZoomRange}
      >
        Live
      </Button>
    </Box>
  );

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: 1.5, mb: 2,
          display: 'flex', alignItems: 'center', gap: 1,
          flexWrap: 'wrap', flexShrink: 0,
          border: '1px solid', borderColor: 'divider', borderRadius: 2,
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'background.paper',
        }}
      >
        {/* Loki Server Dropdown */}
        <FormControl size="small" sx={{ minWidth: isCompact ? 0 : 220, flex: isCompact ? 1 : 'none' }}>
          <Select
            value={selectedServer}
            onChange={(e) => onServerChange(e.target.value)}
            displayEmpty
            renderValue={(value) => {
              if (!value) {
                return (
                  <Typography color="text.secondary" variant="body2">
                    {lokiServers.length === 0 ? serverListEmptyHint : 'Select Loki Server'}
                  </Typography>
                );
              }
              const server = lokiServers.find(s => s._id === value);
              if (!server) return value;
              const logo = MODULE_LOGO_MAP[server.moduleType];
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {logo && <img src={`${LOGO_BASE}/${logo}`} alt={server.moduleType} style={{ width: 20, height: 20, objectFit: 'contain' }} />}
                  <Typography variant="body2">{server.name}</Typography>
                </Box>
              );
            }}
          >
            {lokiServers.length === 0 && (
              <MenuItem disabled value="">{serverListEmptyHint}</MenuItem>
            )}
            {lokiServers.map(server => {
              const logo = MODULE_LOGO_MAP[server.moduleType];
              return (
                <MenuItem key={server._id} value={server._id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {logo && <img src={`${LOGO_BASE}/${logo}`} alt={server.moduleType} style={{ width: 20, height: 20, objectFit: 'contain' }} />}
                    {server.name}
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        {/* Select Labels Button — only for Loki servers */}
        {isSelectedServerLoki && (
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            startIcon={<LabelIcon sx={{ fontSize: 16 }} />}
            onClick={onOpenLabelDialog}
            disabled={!selectedServer}
            sx={{ textTransform: 'none', fontWeight: 600, px: 1.5, whiteSpace: 'nowrap' }}
          >
            Labels
            {selectedLabelCount > 0 && (
              <Box
                component="span"
                sx={{
                  ml: 0.75, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  minWidth: 20, height: 20, px: 0.5, borderRadius: '10px',
                  fontSize: '0.7rem', fontWeight: 700, lineHeight: 1,
                  backgroundColor: 'warning.main', color: 'warning.contrastText',
                }}
              >
                {selectedLabelCount}
              </Box>
            )}
          </Button>
        )}

        {/* Compact: Burger menu for time range controls */}
        {isCompact && (
          <IconButton
            onClick={() => setToolbarDialogOpen(true)}
            size="small"
            sx={{
              ml: 'auto',
              border: '1px solid', borderColor: 'divider',
              '&:hover': { backgroundColor: 'action.hover' },
            }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Desktop: Right-side controls inline */}
        {!isCompact && (
          <>
            <Box sx={{ flex: 1 }} />
            {timeRangeControls}
            {actionButtons}
          </>
        )}

        {/* Compact: Run Query + Live buttons as second row */}
        {isCompact && (
          <Box sx={{ display: 'flex', width: '100%', gap: 1 }}>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            startIcon={<Sync sx={{ fontSize: 16 }} />}
            onClick={onRunQuery}
            sx={{ flex: 1, textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
            disabled={!selectedServer || logQueryLoading}
          >
            {logQueryLoading ? 'Loading...' : 'Run query'}
          </Button>
          <LiveRefreshCountdown nextFetchAt={nextFetchAt} />
          <Button
            variant={isLive ? 'contained' : 'outlined'}
            color={isLive ? 'success' : 'secondary'}
            size="small"
            startIcon={isLive ? <Pause sx={{ fontSize: 16 }} /> : <PlayArrow sx={{ fontSize: 16 }} />}
            onClick={onToggleLive}
            sx={{
              flex: 1, textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap',
              ...(isLive && {
                animation: `${liveBlinkAnimation} 1.5s ease-in-out infinite`,
                backgroundColor: 'success.main',
                color: '#fff',
                '&:hover': { backgroundColor: 'success.dark' },
              }),
            }}
            disabled={!selectedServer || !!chartZoomRange}
          >
            Live
          </Button>
          </Box>
        )}
      </Paper>

      {/* Mobile Toolbar Dialog */}
      <Dialog open={toolbarDialogOpen} onClose={() => setToolbarDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Query Controls</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>Time Range</Typography>
            {timeRangeControls}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToolbarDialogOpen(false)} variant="outlined" color="secondary">Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

LogToolbar.displayName = 'LogToolbar';
